import { Injectable } from '@angular/core';
import { NzConfigService } from 'ng-zorro-antd/core/config';

/**
 * Ponte TemAPI -> NG-Zorro.
 *
 * Os componentes padrão do NG-Zorro que não têm override manual no
 * temapi-theme.scss usam as variáveis --ant-* (o app importa o build
 * ng-zorro-antd.variable.min.css). Este serviço regenera essas variáveis
 * via NzConfigService.set('theme', ...) — o NG-Zorro calcula a paleta
 * completa (tons de hover/active/bg) a partir das cores base — sempre
 * que o tema TemAPI muda, inclusive no tema personalizado.
 *
 * As cores dos temas fixos espelham os literais do temapi-theme.scss.
 */

export interface NzBridgeColors {
    primary: string;
    success: string;
    warning: string;
    error: string;
}

@Injectable({ providedIn: 'root' })
export class NzThemeBridgeService {

    constructor(private nzConfigService: NzConfigService) { }

    private readonly fixedThemes: { [layout: string]: NzBridgeColors } = {
        'light':        { primary: '#134465', success: '#2ea06b', warning: '#e0a13c', error: '#d4544f' },
        'light_modern': { primary: '#134465', success: '#2ea06b', warning: '#e0a13c', error: '#d4544f' },
        'dark':         { primary: '#3d7fae', success: '#4dbd8b', warning: '#f0b95a', error: '#e57373' },
        // vault sobrepõe só a cor de ação; success/warn/error herdam do dark
        'vault':        { primary: '#2DD4BF', success: '#4dbd8b', warning: '#f0b95a', error: '#e57373' },
        // warm: terracota como ação, semânticas em tons terrosos
        'warm':         { primary: '#9c4b2a', success: '#5b8c4f', warning: '#d98a00', error: '#c0392b' },
        // violet: violeta profundo como ação
        'violet':       { primary: '#6d28d9', success: '#2ea06b', warning: '#e0a13c', error: '#dc2f52' },
        // orange: laranja queimado como ação
        'orange':       { primary: '#c2410c', success: '#2f9e5f', warning: '#ca8a04', error: '#d4544f' },
        // brasil: verde da bandeira como ação, sucesso em verde distinto
        'brasil':       { primary: '#17703F', success: '#2E9E5B', warning: '#E08A1E', error: '#C4342B' },
        // zorro: paleta original do Ant Design
        'zorro':        { primary: '#1890ff', success: '#52c41a', warning: '#faad14', error: '#ff4d4f' },
        // bootstrap: paleta do Bootstrap 5.3
        'bootstrap':    { primary: '#0d6efd', success: '#198754', warning: '#ffc107', error: '#dc3545' },
    };

    /** Sincroniza as variáveis --ant-* com um tema fixo (light/light_modern/dark/vault). */
    syncFixed(layout: string): void {
        this.syncColors(this.fixedThemes[layout] || this.fixedThemes['light']);
    }

    /** Sincroniza as variáveis --ant-* com cores arbitrárias (tema personalizado). */
    syncColors(colors: NzBridgeColors): void {
        if (colors == null) {
            return;
        }
        this.nzConfigService.set('theme', {
            primaryColor: colors.primary,
            successColor: colors.success,
            warningColor: colors.warning,
            errorColor: colors.error,
            infoColor: colors.primary,
            processingColor: colors.primary,
        });
    }
}
